/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';

import { AutocompleteFormField } from '@components/form-fields/AutocompleteFormField';
import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '@features/production/proposal/drawers/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { MAIN_THEME } from '@styles';
import {
  getLineCategory,
  hasRisk,
  removeObjectAtIndex
} from '@utils/helper-utils';

import {
  useGetBusinessPartnerPersons,
  useGetPersonAddressList
} from '../amendment.slice';
import { FormValues, InsuredFormRow } from '../types';

import { FormikErrors, useFormikContext } from 'formik';
import { LineCategory } from 'src/enums';
import { makeStyles } from 'tss-react/mui';

type Status = 'NEW' | 'IN_PROGRESS' | 'CONFIRMED' | 'CANCELLED';

export type AmendmentInsuredRow = {
  id: string;
  personId: string;
  code?: string;
  fullName: string;
  order: string;
  address: string;
  addressId: string; // ✅ CRITICAL FIX: Added addressId to type
  addressDetails: string;
  limitOfCover?: string | number;
};

type Props = {
  currencySymbol?: string;
  primaryBPId: string;
  lineCode?: string;
  status: Status;
  amendmentId: string;
  maxNumber: number;
  personsOptions: Record<string, string>;
  setPersonsOptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  translateNameOnLicense?: (
    enTxt: string,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<any>>
  ) => Promise<void>;
};

const useStyles = makeStyles<{
  maxNumber: number;
  showLimitOfCover: boolean;
}>()((_, { maxNumber, showLimitOfCover }) => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative'
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns:
      maxNumber > 1
        ? showLimitOfCover
          ? '0.5fr 2fr 2fr 1.2fr'
          : '0.5fr 2fr 2fr'
        : showLimitOfCover
          ? '2fr 2fr 1.2fr'
          : '2fr 2fr',
    gap: '2%',
    alignItems: 'center',
    '& > *': {
      width: '100%'
    }
  },

  addBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    margin: '20px auto 0',
    display: 'block'
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    position: 'absolute',
    top: '8px',
    right: '0',
    margin: 0
  },
  clearBtn: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 !important'
  }
}));

const formatAddress = (it: any) => {
  const { building, street, city } = it?.address || {};
  const country = it?.country?.title;
  const zone = it?.zone?.title;

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

const formatPersonLabel = (code?: string | null, fullName?: string | null) => {
  const fn = fullName ?? '';
  const c = code ?? '';
  if (c) return `${c} - ${fn}`;

  return fn;
};

const InsuredsWidgetAtom: React.FC<Props> = ({
  currencySymbol,
  primaryBPId,
  lineCode,
  status,
  amendmentId,
  maxNumber,
  personsOptions,
  setPersonsOptions,
  translateNameOnLicense
}) => {
  const showLimitOfCover = hasRisk(lineCode);
  const { classes } = useStyles({ maxNumber, showLimitOfCover });

  const isStatusDisabled = status === 'CONFIRMED' || status === 'CANCELLED';

  const lineCodeNumber = lineCode ? parseInt(lineCode, 10) : undefined;
  const isLineCode43 = lineCodeNumber === 43;

  const isInsuredFieldDisabled = isStatusDisabled || isLineCode43;

  const isAddressFieldDisabled = true;

  const isDisabled = isStatusDisabled;

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleBlur
  } = useFormikContext<FormValues>();

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState<number | null>(
    null
  );
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState<
    number | null
  >(null);

  const insureds = values?.insureds;

  const insuredErrors = (errors?.insureds as any) ?? {};
  const insuredTouched = (touched?.insureds as any) ?? {};

  const [addressOptions, setAddressOptions] = useState<
    Record<number, Record<string, string>>
  >({});

  const [personsQuery] = useGetBusinessPartnerPersons();
  const [getPersonAddressListLazy] = useGetPersonAddressList();

  // CRITICAL FIX: Single useEffect to properly sync addressOptions
  // This merges existing options with new addresses, never wipes out data
  useEffect(() => {
    setAddressOptions((prev) => {
      const updated = { ...prev };

      insureds.forEach((row, idx) => {
        const addrId = row.addressId;
        const addrText = row.addressDetails;

        // If this insured has an address, make sure it's in the options
        if (addrId && addrText) {
          if (!updated[idx]) {
            updated[idx] = {};
          }
          // Only add if not already present
          if (!updated[idx][addrId]) {
            updated[idx] = {
              ...updated[idx],
              [addrId]: addrText
            };
          }
        }
      });

      return updated;
    });
  }, [
    insureds.map((i) => `${i.id}:${i.addressId}:${i.addressDetails}`).join('|')
  ]);

  const fetchAddresses = async (personId: string) => {
    const res = await getPersonAddressListLazy({
      variables: { selectedPersonID: personId },
      errorPolicy: 'all'
    });
    const items = res?.data?.salesforce?.getPersonAddressList?.items || [];
    const map: Record<string, string> = {};
    let primary = '';
    let primaryText = '';

    items.forEach((it: any) => {
      const { building, street, city } = it.address || {};
      const country = it?.country?.title;
      const zone = it?.zone?.title;
      const a = [building, street, zone, city, country]
        .filter(Boolean)
        .join(', ');
      map[String(it.id)] = a;
      if (it.isPrimary && !primary) {
        primary = String(it.id);
        primaryText = a;
      }
    });

    if (!primary && items[0]) {
      const { building, street, city } = items[0].address || {};
      const country = items[0]?.country?.title;
      const zone = items[0]?.zone?.title;
      primary = String(items[0].id);
      primaryText = [building, street, zone, city, country]
        .filter(Boolean)
        .join(', ');
    }

    return { map, primary, primaryText };
  };

  const handlePersonDrawerSuccess = async (
    rowIndex: number,
    personId: string,
    fullName: string
  ) => {
    const label = formatPersonLabel(undefined, fullName);

    setPersonsOptions((prev) => ({
      ...prev,
      [personId]: label
    }));

    await setFieldValue(`insureds[${rowIndex}].fullName`, fullName, false);
    await setFieldValue(`insureds[${rowIndex}].id`, personId, false);
    const { primary, map, primaryText } = await fetchAddresses(
      String(personId)
    );
    setAddressOptions((prev) => ({
      ...prev,
      [rowIndex]: map
    }));
    await setFieldValue(
      `insureds[${rowIndex}].addressId`,
      primary || '',
      false
    );
    await setFieldValue(
      `insureds[${rowIndex}].addressDetails`,
      primaryText || '',
      false
    );

    // ✅ UPDATE MOTOR WIDGET: Update motor widget name on license for motor policies
    // Only update for the first insured (rowIndex === 0) and for motor line types
    const isMotor = getLineCategory(lineCode || '') === LineCategory.Motor;

    if (isMotor && rowIndex === 0 && fullName) {
      // Extract name part from label (format is "CODE - NAME" or just "NAME")
      const namePart = label.includes(' - ') ? label.split(' - ')[1] : label;

      // Update motor widget's name on license field
      await setFieldValue('motorDetails.nameOnLicenseEN', namePart, false);

      // Trigger translation to Arabic if function is available
      if (translateNameOnLicense) {
        await translateNameOnLicense(namePart, setFieldValue);
      }
    }

    setNewPersonDrawerOpen(null);
  };

  // FIX BUG 2 & 3: Update addressDetails when address is added
  const handleAddressDrawerSuccess = async (
    rowIndex: number,
    addressId: string,
    addressText: string
  ) => {
    const personId = insureds[rowIndex].id;

    // Update address options for current row
    setAddressOptions((prev) => ({
      ...prev,
      [rowIndex]: { ...prev[rowIndex], [addressId]: addressText }
    }));

    // Update current insured's address
    await setFieldValue(`insureds[${rowIndex}].addressId`, addressId, false);
    await setFieldValue(
      `insureds[${rowIndex}].addressDetails`,
      addressText,
      false
    );

    // Update address in all other sections where the same person is used
    // Check beneficiaries
    const beneficiaries = (values as any)?.beneficiaries || [];
    for (let i = 0; i < beneficiaries.length; i++) {
      if (beneficiaries[i].id === personId) {
        await setFieldValue(`beneficiaries[${i}].addressId`, addressId, false);
        await setFieldValue(
          `beneficiaries[${i}].addressDetails`,
          addressText,
          false
        );
      }
    }

    // Check payer
    if ((values as any)?.payerOwner?.payer?.id === personId) {
      await setFieldValue('payerOwner.payer.addressId', addressId, false);
      await setFieldValue(
        'payerOwner.payer.addressDetails',
        addressText,
        false
      );
    }

    // Check owner
    if ((values as any)?.payerOwner?.owner?.id === personId) {
      await setFieldValue('payerOwner.owner.addressId', addressId, false);
      await setFieldValue(
        'payerOwner.owner.addressDetails',
        addressText,
        false
      );
    }

    // Update other insureds with the same person
    for (let i = 0; i < insureds.length; i++) {
      if (i !== rowIndex && insureds[i].id === personId) {
        await setFieldValue(`insureds[${i}].addressId`, addressId, false);
        await setFieldValue(
          `insureds[${i}].addressDetails`,
          addressText,
          false
        );
      }
    }

    setNewAddressDrawerOpen(null);
  };

  const onAddRow = async () => {
    if (isInsuredFieldDisabled || insureds.length >= maxNumber) return;

    const next: AmendmentInsuredRow = {
      id: '',
      personId: '',
      code: '',
      fullName: '',
      order: String(insureds.length + 1),
      address: '',
      addressId: '', // ✅ CRITICAL FIX: Must initialize to avoid controlled/uncontrolled error
      addressDetails: '',
      limitOfCover: showLimitOfCover ? '' : undefined
    };

    const newValues = [...insureds, next];
    await setFieldValue('insureds', newValues, false);

    const isMotor = getLineCategory(lineCode || '') === LineCategory.Motor;
    if (isMotor && insureds.length === 0) {
      await setFieldValue('motorDetails.nameOnLicenseEN', '', false);
      await setFieldValue('motorDetails.nameOnLicenseAR', '', false);
    }
  };

  // FIX BUG 1: Update order when row is deleted
  const onDeleteRow = async (idx: number) => {
    if (isInsuredFieldDisabled || insureds.length <= 1) return;

    const nv = removeObjectAtIndex(insureds, idx);
    // Update order numbers
    nv.forEach((r, i) => {
      r.order = String(i + 1);
    });
    await setFieldValue('insureds', nv, false);

    setAddressOptions((prev) => {
      const updated: Record<number, Record<string, string>> = {};
      Object.keys(prev).forEach((key) => {
        const k = Number(key);
        if (k < idx) updated[k] = prev[k];
        else if (k > idx) updated[k - 1] = prev[k];
      });

      return updated;
    });

    const isMotor = getLineCategory(lineCode || '') === LineCategory.Motor;
    if (isMotor && idx === 0 && nv.length > 0) {
      const newFirstInsured = nv[0];
      if (newFirstInsured?.id) {
        const label = personsOptions[newFirstInsured.id];
        if (label) {
          const namePart = label.includes(' - ')
            ? label.split(' - ')[1]
            : label;
          await setFieldValue('motorDetails.nameOnLicenseEN', namePart, false);

          if (translateNameOnLicense) {
            await translateNameOnLicense(namePart, setFieldValue);
          }
        }
      } else {
        await setFieldValue('motorDetails.nameOnLicenseEN', '', false);
        await setFieldValue('motorDetails.nameOnLicenseAR', '', false);
      }
    }
  };

  const handleInsuredChange = async (v: string, idx: number) => {
    await setFieldValue(`insureds[${idx}].id`, v ?? '', false);
    const { map, primary, primaryText } = await fetchAddresses(v);

    // ✅ CRITICAL FIX: MERGE fetched addresses with existing ones, don't replace!
    setAddressOptions((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), ...map } // Merge instead of replace
    }));

    await setFieldValue(`insureds[${idx}].addressId`, primary ?? '', false);
    await setFieldValue(
      `insureds[${idx}].addressDetails`,
      primary ? primaryText : '',
      false
    );

    // NEW CODE: Update motor widget name on license for motor policies
    // Only update for the first insured (idx === 0) and for motor line types
    const isMotor = getLineCategory(lineCode || '') === LineCategory.Motor;

    if (isMotor && idx === 0 && v) {
      const label = personsOptions[v];
      if (label) {
        // Extract name part (format is "CODE - NAME")
        const namePart = label.includes(' - ') ? label.split(' - ')[1] : label;

        // Update motor widget's name on license field
        await setFieldValue('motorDetails.nameOnLicenseEN', namePart, false);

        // Trigger translation to Arabic if function is available
        if (translateNameOnLicense) {
          await translateNameOnLicense(namePart, setFieldValue);
        }
      }
    }
  };

  const renderRow = (row: InsuredFormRow, idx: number) => {
    const rowErrors = insuredErrors?.[idx];
    const rowTouched = insuredTouched?.[idx];

    const selectedOthers = insureds.filter((_, i) => i !== idx);
    const takenIds = new Set(
      selectedOthers.map((r) => r.id).filter(Boolean) as string[]
    );

    const labelFromPool =
      row.id && personsOptions[row.id] ? personsOptions[row.id] : '';
    const fallbackLabel = formatPersonLabel(row.code, row.fullName) || '';

    const currentLabel = labelFromPool || fallbackLabel;

    const initialOptions: Record<string, string> = {};

    if (row.id && currentLabel) {
      initialOptions[row.id] = currentLabel;
    }

    Object.entries(personsOptions || {}).forEach(([id, label]) => {
      if (id === row.id) return;
      if (takenIds.has(id)) return;
      initialOptions[id] = label;
    });

    return (
      <div key={idx} className={classes.container}>
        <div className={classes.clearBtn}>
          {!isInsuredFieldDisabled && insureds.length > 1 && (
            <button
              className={classes.removeBtn}
              onClick={() => onDeleteRow(idx)}
            >
              <ClearIcon fontSize="small" />
            </button>
          )}
        </div>

        <div className={classes.fieldRow}>
          {maxNumber > 1 && (
            <NumberInputFormField
              name="order"
              title="Order*"
              placeholder="Order"
              value={row.order}
              error={rowTouched?.order ? (rowErrors?.order as string) : ''}
              onChange={(e) =>
                setFieldValue(`insureds[${idx}].order`, e.target.value, true)
              }
              disabled={isDisabled}
              material
            />
          )}

          <WithLinkFormField
            linkText={isInsuredFieldDisabled ? '' : 'Add Person'}
            onLinkClick={() => setNewPersonDrawerOpen(idx)}
          >
            <AutocompleteFormField
              name={`insureds[${idx}].id`}
              title="Insured*"
              placeholder="Type to search for Person"
              value={row.id}
              error={rowTouched?.id ? rowErrors?.id : ''}
              material
              disabled={isInsuredFieldDisabled}
              onChange={(v) => handleInsuredChange(v, idx)}
              onBlur={(_, e) => handleBlur(e)}
              initialOptions={initialOptions}
              fetchOptions={async (
                searchKeyword
              ): Promise<Record<string, string>> => {
                if (!primaryBPId) return {};
                const res = await personsQuery({
                  variables: {
                    mainbusinessPartnerID: primaryBPId,
                    searchKeyword: (searchKeyword ?? '').trim()
                  }
                });

                const items =
                  res?.data?.salesforce?.getBusinessPartnerPersons || [];

                const selectedIds = new Set(
                  insureds
                    .filter((_, i) => i !== idx)
                    .map((r) => r.id)
                    .filter(Boolean) as string[]
                );

                const filtered = items.filter(
                  (it: any) => !selectedIds.has(it.id)
                );

                const options: Record<string, string> = {};
                filtered.forEach((it: any) => {
                  const label = formatPersonLabel(it.code, it.fullName);
                  options[it.id] = label;
                });

                setPersonsOptions((prev) => ({ ...prev, ...options }));

                const current = insureds[idx];
                if (current?.id) {
                  const matched = items.find((it: any) => it.id === current.id);
                  if (matched) {
                    await setFieldValue(
                      `insureds[${idx}].code`,
                      matched.code ?? '',
                      false
                    );
                    await setFieldValue(
                      `insureds[${idx}].fullName`,
                      matched.fullName ?? '',
                      false
                    );
                  }
                }

                return options;
              }}
            />
          </WithLinkFormField>

          <WithLinkFormField
            linkText={isDisabled ? '' : 'Add Address'}
            onLinkClick={() => setNewAddressDrawerOpen(idx)}
          >
            <SelectFormField
              name={`insureds[${idx}].addressId`}
              title="Insured Address*"
              placeholder="Insured Address"
              value={row.addressId}
              error={
                (rowTouched?.addressId as any)
                  ? (rowErrors?.addressId as any)
                  : ''
              }
              disabled={isAddressFieldDisabled}
              material
              onChange={(v) =>
                setFieldValue(`insureds[${idx}].addressId`, v, true)
              }
              selectOptions={addressOptions[idx] || {}}
            />
          </WithLinkFormField>

          {showLimitOfCover && (
            <CurrencyFormField
              name={`insureds[${idx}].limitOfCover`}
              title="Limit of Cover*"
              placeholder="Limit of Cover"
              value={row.limitOfCover ?? ''}
              error={
                rowTouched?.limitOfCover
                  ? (rowErrors?.limitOfCover as string)
                  : ''
              }
              onChange={(e) =>
                setFieldValue(
                  `insureds[${idx}].limitOfCover`,
                  e.target.value,
                  true
                )
              }
              onBlur={(e) => handleBlur(e)}
              disabled={isDisabled}
              material
              currencySymbol={currencySymbol}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <WidgetPaper>
      <WidgetSection
        title="Insureds"
        hasTitleSpecificDesign={false}
        useSeparator={false}
      >
        {insureds?.map((row, idx) => renderRow(row, idx))}

        {newPersonDrawerOpen !== null && (
          <ProposalPolicyPersonDrawer
            open
            onClose={() => setNewPersonDrawerOpen(null)}
            section="insured"
            lineExternalCode={lineCode}
            onSubmitSuccess={(person) => {
              const fullName = `${person.code} - ${person.fullName}`;
              handlePersonDrawerSuccess(
                newPersonDrawerOpen,
                person.id,
                fullName
              );
            }}
            mainBusinessPartnerId={primaryBPId}
          />
        )}

        {newAddressDrawerOpen !== null && (
          <PersonAddressDrawer
            open
            isProposal
            onClose={() => setNewAddressDrawerOpen(null)}
            personId={insureds[newAddressDrawerOpen].id}
            onSubmitSuccess={(addrId, addr) =>
              handleAddressDrawerSuccess(newAddressDrawerOpen, addrId, addr)
            }
            onSuccess={() => undefined}
          />
        )}

        {!isInsuredFieldDisabled && insureds.length < maxNumber && (
          <button className={classes.addBtn} onClick={onAddRow}>
            <AddCircleOutlineIcon fontSize="small" color="error" />
          </button>
        )}
      </WidgetSection>
    </WidgetPaper>
  );
};

export { InsuredsWidgetAtom };
